import React from "react";
import Select from "react-select";
import { cn } from "../../lib/cn";

/**
 * Универсальный Select с интеграцией дизайн-системы.
 */
export const SelectComponent = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Выберите...",
  isClearable = false,
  isSearchable = true,
  isDisabled = false,
  isMulti = false,
  className = "",
  error = "",
  label = "",
  id = "",
  size = "md",
  required = false,
  menuPortalTarget = null,
  menuPosition = "absolute",
  ...props
}) => {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

  const heights = {
    sm: "var(--input-height)",
    md: "var(--input-height)",
    lg: "var(--input-height)",
  };

  const fontSizes = {
    sm: "var(--font-size-sm)",
    md: "var(--font-size-base)",
    lg: "var(--font-size-lg)",
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      height: heights[size] || heights.md,
      minHeight: heights[size] || heights.md,
      backgroundColor: "var(--color-bg-tertiary)",
      // Делаем обводку заметной в обеих темах
      borderColor: error
        ? "var(--color-accent-danger) !important"
        : state.isFocused
          ? "var(--color-primary) !important"
          : "var(--color-border) !important", // Use the new visible border variable
      borderWidth: "1px !important", // Standardize to 1px
      borderRadius: "var(--radius-md)",
      boxShadow: state.isFocused
        ? "0 0 0 2px var(--color-primary-light)"
        : "0 1px 2px rgba(0,0,0,0.04)",
      fontSize: fontSizes[size] || fontSizes.md,
      padding: "0 4px",
      cursor: isDisabled ? "not-allowed" : "pointer",
      opacity: isDisabled ? 0.6 : 1,
      "&:hover": {
        borderColor: "var(--color-primary-light) !important",
      },
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)", // Keep transition
    }),

    valueContainer: (base) => ({
      ...base,
      padding: "0 12px",
    }),

    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: "var(--color-text-primary) !important",
      fontWeight: "600",
    }),

    placeholder: (base) => ({
      ...base,
      color: "var(--color-text-tertiary) !important",
      fontWeight: "500",
    }),

    singleValue: (base) => ({
      ...base,
      color: "var(--color-text-primary) !important",
      fontWeight: "700",
    }),

    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--color-primary) !important"
        : state.isFocused
          ? "var(--color-primary-light) !important"
          : "transparent !important",
      color: state.isSelected
        ? "white !important"
        : "var(--color-text-primary) !important",
      fontSize: fontSizes[size] || fontSizes.md,
      fontWeight: state.isSelected ? "700" : "600",
      cursor: "pointer",
      padding: "12px 14px",
      margin: "2px 0",
      borderRadius: "8px",
      borderBottom: "none",
      "&:active": {
        backgroundColor: "var(--color-primary) !important",
      },
    }),

    menu: (base) => ({
      ...base,
      backgroundColor: "var(--color-bg-secondary) !important",
      borderRadius: "12px",
      boxShadow: "0 20px 50px -12px rgba(0, 0, 0, 0.45)",
      zIndex: 9999,
      border: "1px solid var(--color-border)",
      padding: "6px",
      marginTop: "6px",
      backdropFilter: "blur(20px)",
      overflow: "visible", // Позволяем контенту не обрезаться
      maxHeight: "340px",
    }),

    menuList: (base) => ({
      ...base,
      backgroundColor: "transparent !important",
      padding: "0",
    }),

    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),

    indicatorSeparator: () => ({ display: "none" }),

    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--color-text-tertiary) !important",
      "&:hover": { color: "var(--color-text-primary) !important" },
    }),

    clearIndicator: (base) => ({
      ...base,
      color: "var(--color-text-tertiary) !important",
      "&:hover": { color: "var(--color-accent-danger) !important" },
    }),
  };

  // Merge custom styles with props.styles
  const mergedStyles = React.useMemo(() => {
    const finalStyles = { ...customStyles };
    const propStyles = props.styles || {};

    // Get all unique keys from both style objects
    const allKeys = new Set([
      ...Object.keys(finalStyles),
      ...Object.keys(propStyles),
    ]);

    allKeys.forEach((key) => {
      const original = finalStyles[key];
      const override = propStyles[key];

      if (original && override) {
        finalStyles[key] = (base, state) => {
          const baseResult = original(base, state);
          const overrideResult =
            typeof override === "function" ? override(base, state) : override;
          return {
            ...baseResult,
            ...overrideResult,
          };
        };
      } else if (override) {
        finalStyles[key] = override;
      }
    });

    return finalStyles;
  }, [props.styles, size, error, isDisabled, customStyles]);

  return (
    <div className={cn("w-full mb-4", className)}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-semibold text-[var(--color-text-secondary)] mb-1.5"
        >
          {label}
          {required && (
            <span className="text-[var(--color-accent-danger)] ml-1">*</span>
          )}
        </label>
      )}
      <Select
        id={selectId}
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        isMulti={isMulti}
        styles={mergedStyles}
        classNamePrefix="react-select"
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
        {...props}
      />
      {error && (
        <p className="mt-1.5 text-xs font-medium text-[var(--color-accent-danger)]">
          {error}
        </p>
      )}
    </div>
  );
};

export default SelectComponent;
